import React from "react";
import { ADVERTISER } from "../../../../core/theme/reportTheme";
import { CHANNEL_NAMES } from "../../../../core/data/marketOverviewText";
import { channelGroups } from "../../../../core/data/channelOrder";
import { factParts, factText, soFar } from "../../../../core/data/advertiserOverviewText";
import Expandable from "../../components/Expandable";
import CopyResultButton from "../../components/CopyResultButton";
import AdaObserved from "../../components/AdaObserved";
import { PinnedCard, VisibilityCard, SearchesSeenCard, DeviceCard } from "./components/TopCards";
import ChannelCard, { NotSearchedTile } from "./components/ChannelCard";
import { useChannelStrips, ChannelStrip } from "../../components/ChannelStrip";
import ShowUpGrid from "./components/ShowUpGrid";
import RankPanel from "./components/RankPanel";
import RivalsPanel from "./components/RivalsPanel";
import PickerPanel from "./components/PickerPanel";

// Not part of Google's page: their own "Other platforms" group.
const OTHER_CHANNELS = ["ai", "bing"];

// Advertiser Overview: draws an advertiserOverview() result and nothing else.
// The page scrolls, like Market Overview; panels scroll inside themselves.
// Top to bottom: four cards · channel tiles (full width, the same order as
// the tabs and Market Overview's page layout: seen in Google's order, then
// none seen, then AI Overview and Bing as slim "not searched" tiles) · AdA observed ·
// "Where you rank" and "Who you're up against" side by side · "Where you show up".
// Nothing pinned: the advertiser picker.
export default function AdvertiserPage({ advertiser: result, overview, onSetType, onPin, onShowTab, onOpenLinkPanel, highlightKeys }) {
  const strips = useChannelStrips();
  if (!result.pinned) {
    return (
      <div className="h-full flex flex-col min-h-0 max-w-3xl mx-auto w-full">
        <Expandable className="flex-1 min-h-0 flex flex-col" name="advertiser picker">
          {button => <PickerPanel result={result} onPin={onPin} cornerButton={button} highlightKeys={highlightKeys} />}
        </Expandable>
      </div>
    );
  }

  const card = (name, draw, slot = ADVERTISER.cardSlot) => (
    <Expandable className={slot} name={name}>{(button, expanded) => draw(button, expanded)}</Expandable>
  );
  // The channel order comes from the Market Overview result for the same filters.
  // Two groups, titled as on Market Overview: Google's page (top to bottom)
  // and Other platforms (AI Overview, Bing). A channel nobody appeared in, or
  // one not searched yet, is a narrow strip that opens on hover.
  const channels = channelGroups(overview.serpMap);
  const isOther = c => OTHER_CHANNELS.includes(c);
  const googleTiles = [
    ...channels.seen.filter(c => !isOther(c)).map(c => [c, !result.channels[c]]),
    ...channels.noneSeen.filter(c => !isOther(c)).map(c => [c, true]),
    ...channels.notChecked.filter(c => !isOther(c)).map(c => [c, true]),
  ];
  const otherTiles = channels.notChecked.filter(isOther).map(c => [c, true]);
  // order: 1, 2, 3... down Google's page, as on Market Overview's page layout tiles.
  const channelTile = ([c, isStrip]) => {
    const seenAt = channels.seen.indexOf(c);
    let body;
    if (!strips.open(c, isStrip)) {
      const why = channels.notChecked.includes(c) ? "not searched yet" : "none seen";
      body = <ChannelStrip channel={c} name={CHANNEL_NAMES[c]} dashed title={`${CHANNEL_NAMES[c]}: ${why}`} />;
    } else if (channels.notChecked.includes(c)) {
      body = <div className={ADVERTISER.channelSlot}><NotSearchedTile channel={c} /></div>;
    } else {
      body = card(`${CHANNEL_NAMES[c]} card`, (b, e) => <ChannelCard channel={c} result={result} order={seenAt >= 0 && result.channels[c] ? seenAt + 1 : undefined} expandButton={b} expanded={e} />, ADVERTISER.channelSlot);
    }
    return <div key={c} {...strips.slot(c, isStrip)}>{body}</div>;
  };
  const facts = (
    <AdaObserved
      facts={result.facts.map((f, i) => ({ id: `${i}-${f.claim}`, ...factParts(f), title: factText(f) }))}
      suffix={soFar(result)}
      labelTitle={result.facts.map(factText).join("\n")}
      right={<CopyResultButton result={result} />}
    />
  );

  return (
    <div className={ADVERTISER.page}>
      <div className={ADVERTISER.cardRow}>
        {card("Pinned advertiser", (b, e) => <PinnedCard result={result} onSetType={onSetType} onOpenLinkPanel={onOpenLinkPanel} expandButton={b} expanded={e} />)}
        {card("Visibility", (b, e) => <VisibilityCard result={result} expandButton={b} expanded={e} />)}
        {card("Searches seen in", (b, e) => <SearchesSeenCard result={result} expandButton={b} expanded={e} />)}
        {card("By device", (b, e) => <DeviceCard result={result} expandButton={b} expanded={e} />)}
      </div>

      <div className={ADVERTISER.channelRow}>
        <div className={ADVERTISER.channelGroup} style={{ flexGrow: strips.groupWeight(googleTiles), flexBasis: 0 }}>
          <p className={ADVERTISER.channelGroupTitle}>Google page layout · top to bottom</p>
          <div className={ADVERTISER.channelTiles}>{googleTiles.map(channelTile)}</div>
        </div>
        {otherTiles.length > 0 && (
          <div className={ADVERTISER.channelGroup} style={{ flexGrow: strips.groupWeight(otherTiles), flexBasis: 0 }}>
            <p className={ADVERTISER.channelGroupTitle}>Other platforms</p>
            <div className={ADVERTISER.channelTiles}>{otherTiles.map(channelTile)}</div>
          </div>
        )}
      </div>

      {facts}

      <div className={ADVERTISER.panelRow}>
        <Expandable className={ADVERTISER.panelSlot} name="Where you rank">
          {(b, e) => <RankPanel result={result} expandButton={b} expanded={e} onShowTab={onShowTab} highlightKeys={highlightKeys} />}
        </Expandable>
        <Expandable className={ADVERTISER.panelSlot} name="Who you're up against">
          {(b, e) => <RivalsPanel result={result} expandButton={b} expanded={e} highlightKeys={highlightKeys} />}
        </Expandable>
      </div>

      <Expandable className={ADVERTISER.gridPanelSlot} name="Where you show up">
        {(b, e) => <ShowUpGrid result={result} expandButton={b} expanded={e} />}
      </Expandable>
    </div>
  );
}
